import { api } from "../../api";

export const userApi = api.injectEndpoints({
    endpoints: (build) => ({
        getUserProfile: build.query({
            query: () => `/v1/user/`,
            providesTags: ["UserProfile"],
        }),
        // Saved locations for booking requests
        getLocations: build.query<any, void>({
            query: () => `/v1/user/get-locations`,
        }),
        // Saved kids list for booking requests
        getKids: build.query<any, void>({
            query: () => `/v1/user/get-kids`,
            providesTags: ["Kids"],
        }),
        addLocation: build.mutation<
            any,
            {
                address: string;
                apartment: string;
                complete_address: string;
                full_name: string;
                label: "Home" | "Office" | "Other" | string;
                latitude: string;
                longitude: string;
                user_id: string | number;
            }
        >({
            query: (body) => ({
                url: `/v1/user/add-location`,
                method: "POST",
                body,
            }),
        }),
        postUserRequest: build.mutation({
            query: (data) => {
                return {
                    url: `/v1/user/emergency-contact`,
                    method: "POST",
                    body: data,
                };
            },
        }),
        putUserType: build.mutation({
            query: (data) => {
                return {
                    url: `/v1/user/set-user-type`,
                    method: "PUT",
                    body: data,
                };
            },
        }),
        postAddFamily: build.mutation({
            query: (data) => {
                return {
                    url: `/v1/user/add-family`,
                    method: "POST",
                    body: data,
                };
            },
            invalidatesTags: ["UserProfile", "Kids"],
        }),
        deleteParent: build.mutation({
            query: (data) => {
                // Backend expects DELETE /parent (pending contract).
                return {
                    url: `/v1/user/delete-parent/${data.id}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["UserProfile"],
        }),
        deleteKids: build.mutation({
            query: (data) => {
                // Backend expects DELETE /kids (pending contract).
                return {
                    url: `/v1/user/delete-kid/${data.id}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["UserProfile", "Kids"],
        }),
        updateUserProfile: build.mutation({
            query: (data) => {
                // Backend expects PUT /v1/user/update/{id} with keys:
                // - full_name
                // - image
                return {
                    url: `/v1/user/update/${data.id}`,
                    method: "PUT",
                    body: data.body,
                };
            },
            invalidatesTags: ["UserProfile"],
        }),
        updateKid: build.mutation({
            query: (data) => {
                // Android behavior:
                // - PUT /v1/user/edit-kid/1
                // - kid id is sent in body as `id` (NOT in URL)
                return {
                    url: `/v1/user/edit-kid/1`,
                    method: "PUT",
                    body: data.body,
                };
            },
            invalidatesTags: ["UserProfile", "Kids"],
        }),
        updateParent: build.mutation({
            query: (data) => {
                // Android behavior:
                // - PUT /v1/user/edit-parent/1
                // - parent id is sent in body as `id` (NOT in URL)
                return {
                    url: `/v1/user/edit-parent/1`,
                    method: "PUT",
                    body: data.body,
                };
            },
            invalidatesTags: ["UserProfile"],
        }),
        getFavoritesByUser: build.query<unknown, string | number>({
            query: (userId) => `/v1/user/favorites-by-user/${userId}`,
            providesTags: ["UserFavorites"],
        }),
        addRemoveFavorite: build.mutation<
            unknown,
            { provider_id: number }
        >({
            query: (body) => ({
                url: `/v1/user/add-remove-favorite`,
                method: "POST",
                body,
            }),
            invalidatesTags: ["UserFavorites", "ProviderDetails"],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetUserProfileQuery,
    useGetLocationsQuery,
    useGetKidsQuery,
    useAddLocationMutation,
    usePostUserRequestMutation,
    usePutUserTypeMutation,
    usePostAddFamilyMutation,
    useDeleteParentMutation,
    useDeleteKidsMutation,
    useUpdateUserProfileMutation,
    useUpdateKidMutation,
    useUpdateParentMutation,
    useGetFavoritesByUserQuery,
    useAddRemoveFavoriteMutation,
} = userApi;
